import { ITEM_TYPE, PRODUCT_TYPE } from '@/utils/enum.js';
import { v4 as uuidv4 } from 'uuid';
import SaveDataUtil from '@/utils/saveDataToLocal';
import { JOB_TITLE_NAME } from '@/utils/enum-name';
import { unMaskPrice } from '@/utils/common';
import {
  hydrateSplitAmountsFromApi,
  isMainOrderLineForStackPayload,
  isStackedDiscountLine,
} from '@/utils/voucherDiscountStack';
import { normalizePromotionLabelsFromHydrate } from '@/utils/discountProgramDisplay';
import { resolveAttachLevel } from '@/view/components/bills/helpers/nestAttachItems';

const commonMixin = {
  data() {
    return {
      employee: {
        name: '',
        store: {
          id: '',
          name: '',
          address: '',
          area: {
            id: '',
            name: '',
          },
        },
      },
      jobTitleId: '',
      storeList: [],
    };
  },
  created: function() {},
  methods: {
    handleOrderItemUpsert(orderItems) {
      const itemHandlers = orderItems.map((item) => {
        switch (item.orderItemType) {
          case ITEM_TYPE.MAIN: {
            item.productType = ITEM_TYPE.MAIN;
            break;
          }
          case ITEM_TYPE.GIFT: {
            item.productType = ITEM_TYPE.GIFT;
            break;
          }
          case ITEM_TYPE.ATTACH: {
            item.productType = ITEM_TYPE.ATTACH;
            break;
          }
        }
        item.productPrice = Number(unMaskPrice(item.sellingPriceMask));
        return item;
      });

      const mainItems = itemHandlers.filter(
        (item) => item.orderItemType === ITEM_TYPE.MAIN,
      );
      const relatedItems = itemHandlers.filter(
        (item) => item.orderItemType !== ITEM_TYPE.MAIN,
      );

      const nestedMains = mainItems.map((mainItem) => {
        const gifts = relatedItems.filter(
          (item) =>
            item.belongOrderDetailId === mainItem.id &&
            item.orderItemType === ITEM_TYPE.GIFT,
        );

        const attachs = relatedItems
          .filter(
            (item) =>
              item.belongOrderDetailId === mainItem.id &&
              item.orderItemType === ITEM_TYPE.ATTACH &&
              resolveAttachLevel(item) === 1,
          )
          .map((l1) => ({
            ...l1,
            attachs: relatedItems.filter(
              (l2) =>
                l2.belongOrderDetailId === l1.id &&
                l2.orderItemType === ITEM_TYPE.ATTACH &&
                resolveAttachLevel(l2) === 2,
            ),
          }));
        mainItem.gifts = gifts;
        mainItem.attachs = attachs;
        this.normalizeOrderFlexibleComboLine(mainItem);
        return mainItem;
      });
      return this.expandFlexibleComboOrderMainsToFlatLines(nestedMains);
    },
    /**
     * Giống bill `flattenFlexibleCombo`: mỗi dòng trong comboItems → một MAIN riêng trong payload order.
     * Dòng đầu giữ `id` cũ để gifts/attachs (belongOrderDetailId) không đứt.
     */
    expandFlexibleComboOrderMainsToFlatLines(mainItems) {
      const out = [];
      for (const mainItem of mainItems) {
        const cis = mainItem.comboItems;
        const hasFlexCombo =
          mainItem.flexibleComboId != null &&
          mainItem.flexibleComboId !== '' &&
          Array.isArray(cis) &&
          cis.length > 0;

        if (!hasFlexCombo) {
          if (Array.isArray(mainItem.comboItems) && mainItem.comboItems.length === 0) {
            delete mainItem.comboItems;
          }
          out.push(mainItem);
          continue;
        }

        const parentGifts = mainItem.gifts || [];
        const parentAttachs = mainItem.attachs || [];
        const flexComboName =
          mainItem.flexibleComboName || mainItem.productName || '';
        const flexComboCode =
          mainItem.flexibleComboCode || mainItem.productCode || '';

        cis.forEach((ci, index) => {
          const row = { ...mainItem };
          delete row.label;
          row.id = index === 0 ? mainItem.id : uuidv4();
          row.productId = ci.productId;
          row.productName = ci.productName || ci.name || '';
          row.productCode = ci.productCode || '';
          row.productBarCode = ci.productBarCode || ci.barCode || '';
          row.imageUrl = ci.imageUrl ?? mainItem.imageUrl;
          row.flexibleComboName = flexComboName;
          row.flexibleComboCode = flexComboCode;
          const price = Number(ci.sellingPrice ?? ci.productPrice ?? 0);
          row.productPrice = price;
          row.sellingPrice = price;
          row.sellingPriceMask = price;
          row.quantity = Number(ci.quantity) || 1;
          row.discountAmount = Number(
            unMaskPrice(ci.discountAmount ?? ci.discount ?? 0),
          );
          row.discountType = ci.discountType ?? mainItem.discountType ?? 1;
          row.imeiCode = ci.imeiCode || '';
          row.flexibleComboItemId =
            ci.flexibleComboItemId != null && ci.flexibleComboItemId !== ''
              ? ci.flexibleComboItemId
              : ci.flexibleComboItemID ?? null;
          row.comboItems = [];
          row.gifts = index === 0 ? parentGifts : [];
          row.attachs = index === 0 ? parentAttachs : [];

          if (index > 0) {
            row.voucherId = null;
            row.voucherDetailId = null;
            row.voucherCode = null;
            row.discountProgramId = null;
            row.promotionProgramCode = '';
            row.promotionProgramName = '';
          }

          out.push(row);
        });
      }
      return out;
    },
    /**
     * Chuẩn hoá dòng combo linh hoạt trước khi gửi order API:
     * - productId từng dòng con: string (đồng bộ orderItems khác)
     * - flexibleComboItemId, quantity, tiền số học
     */
    normalizeOrderFlexibleComboLine(mainItem) {
      if (!mainItem?.flexibleComboId || !Array.isArray(mainItem.comboItems)) {
        return;
      }
      if (!mainItem.flexibleComboName) {
        mainItem.flexibleComboName = mainItem.productName || '';
      }
      if (!mainItem.flexibleComboCode) {
        mainItem.flexibleComboCode = mainItem.productCode || '';
      }
      const fcid = Number(mainItem.flexibleComboId);
      if (!Number.isNaN(fcid)) {
        mainItem.flexibleComboId = fcid;
      }
      mainItem.comboItems = mainItem.comboItems.map((ci) => {
        const pid = ci.productId;
        const productId =
          pid === null || pid === undefined || pid === '' ? pid : String(pid);
        const flexItemId =
          ci.flexibleComboItemId != null && ci.flexibleComboItemId !== ''
            ? ci.flexibleComboItemId
            : ci.flexibleComboItemID ?? null;
        return {
          ...ci,
          productId,
          productName: ci.productName || ci.name || '',
          productCode: ci.productCode || '',
          flexibleComboItemId: flexItemId,
          quantity: Number(ci.quantity) || 1,
          sellingPrice: Number(ci.sellingPrice ?? ci.productPrice ?? 0),
          totalAmount: Number(ci.totalAmount ?? 0),
          discountAmount: Number(ci.discountAmount ?? ci.discount ?? 0),
        };
      });
      const firstLine = mainItem.comboItems.find(
        (ci) => ci.productId != null && ci.productId !== '',
      );
      if (firstLine != null) {
        mainItem.productId = firstLine.productId;
      }
    },
    /**
     * Map one line from Upsert/Vuex shape to legacy `orders/` listOrderItem row (flat + nested gifts/attachs).
     */
    mapUpsertLineToLegacyOrderPayload(el, persistIds) {
      const price = Number(unMaskPrice(el.sellingPriceMask ?? el.productPrice ?? 0));
      const disc = Number(unMaskPrice(el.discountAmount ?? 0));
      const payload = {
        id: persistIds ? el.id : null,
        productId: el.productId,
        productPrice: price,
        quantity: el.quantity,
        discountAmount: disc,
        discountType: el.discountType ?? 1,
        discountProgramId: el.discountProgramId ?? null,
        promotionProgramCode: el.promotionProgramCode ?? null,
        voucherId: el.voucherId ?? null,
        voucherDetailId: el.voucherDetailId ?? null,
        voucherCode: el.voucherCode ?? null,
        flexibleComboId: el.flexibleComboId ?? null,
        flexibleComboItemId: el.flexibleComboItemId ?? null,
        imeiCode: el.imeiCode || '',
        productType: el.orderItemType,
        accessoryGroupId: el.accessoryGroupId,
        accessoryGroupCode: el.accessoryGroupCode,
        promotionProgramId: el.promotionProgramId ?? el.accessoryGroupId ?? null,
        attachLevel: resolveAttachLevel(el),
        repurchasePrice: Number(
          unMaskPrice(el.repurchasePrice ?? el.returnPrice ?? 0),
        ),
        isGiftTaken: el.checked,
        belongOrderDetailId: el.belongOrderDetailId ?? el.parentId,
        note: el.note || '',
      };
      if (isMainOrderLineForStackPayload(el) && isStackedDiscountLine(el)) {
        payload.discountProgramAmount = Number(el.discountProgramAmount ?? 0);
        payload.voucherAmount = Number(el.voucherAmount ?? 0);
      }
      return payload;
    },
    /**
     * Full list for POST/PUT `orders/` after handleOrderItemUpsert.
     */
    formatOrderItemsLegacyApi(nestedMainItems, persistIds) {
      const mapAttachNested = (attach) => {
        const nestedChildren = attach.attachs || [];
        const payload = this.mapUpsertLineToLegacyOrderPayload(attach, persistIds);
        if (nestedChildren.length) {
          payload.attachs = nestedChildren.map(mapAttachNested);
        }
        return payload;
      };
      return nestedMainItems.map((main) => {
        const gifts = (main.gifts || []).map((g) =>
          this.mapUpsertLineToLegacyOrderPayload(g, persistIds),
        );
        const attachs = (main.attachs || []).map(mapAttachNested);
        return {
          ...this.mapUpsertLineToLegacyOrderPayload(main, persistIds),
          gifts,
          attachs,
        };
      });
    },
    /**
     * Gộp các MAIN combo linh hoạt đã flat (cùng flexibleComboId liên tiếp, không có comboItems lồng)
     * thành một dòng hiển thị + comboItems[] — đối lập với expandFlexibleComboOrderMainsToFlatLines lúc lưu.
     */
    mergeConsecutiveFlatFlexComboOrderMains(mainsOrdered) {
      const out = [];
      let i = 0;
      while (i < mainsOrdered.length) {
        const cur = mainsOrdered[i];
        if (!cur.flexibleComboId) {
          out.push(cur);
          i++;
          continue;
        }
        if (Array.isArray(cur.comboItems) && cur.comboItems.length > 0) {
          out.push(cur);
          i++;
          continue;
        }
        const fcid = cur.flexibleComboId;
        const group = [];
        while (
          i < mainsOrdered.length &&
          mainsOrdered[i].flexibleComboId === fcid &&
          (!Array.isArray(mainsOrdered[i].comboItems) ||
            mainsOrdered[i].comboItems.length === 0)
        ) {
          group.push(mainsOrdered[i]);
          i++;
        }
        out.push(this.buildSyntheticFlexComboMainFromFlatOrderLines(group));
      }
      return out;
    },
    buildSyntheticFlexComboMainFromFlatOrderLines(groupRows) {
      const first = groupRows[0];
      const serverKeys = groupRows
        .map((r) => String(r.orderItemId ?? r.id))
        .filter((k) => k && k !== 'undefined');
      const totalSimple = groupRows.reduce((s, r) => {
        const qty = Number(r.quantity) || 1;
        const price = Number(r.productPrice ?? r.sellingPrice ?? 0);
        const disc = Number(r.discountAmount ?? 0);
        return s + qty * price - disc;
      }, 0);
      const comboItems = groupRows.map((r) => ({
        id: r.flexibleComboItemId != null ? r.flexibleComboItemId : r.id,
        productId: r.productId,
        productName: r.productName,
        productCode: r.productCode || '',
        productBarCode: r.productBarCode || r.barCode,
        imeiCode: r.imeiCode || '',
        sellingPrice: Number(r.productPrice ?? r.sellingPrice ?? 0),
        productPrice: Number(r.productPrice ?? r.sellingPrice ?? 0),
        quantity: Number(r.quantity) || 1,
        discountAmount: Number(r.discountAmount ?? 0),
        discountType: r.discountType ?? 1,
        flexibleComboItemId: r.flexibleComboItemId ?? null,
        totalAmount:
          r.totalAmount != null
            ? Number(r.totalAmount)
            : Number(r.productPrice ?? 0) * (Number(r.quantity) || 1) -
              Number(r.discountAmount ?? 0),
      }));
      return {
        ...first,
        productName: first.flexibleComboName || first.productName,
        productCode: first.flexibleComboCode || first.productCode || '',
        productBarCode: '',
        flexibleComboName: first.flexibleComboName || null,
        flexibleComboCode: first.flexibleComboCode || null,
        sellingPrice: totalSimple,
        sellingPriceMask: totalSimple,
        productPrice: totalSimple,
        quantity: 1,
        discountAmount: 0,
        discountType: first.discountType ?? 1,
        flexibleComboId: first.flexibleComboId,
        flexibleComboItemId: null,
        comboItems,
        orderItemType: ITEM_TYPE.MAIN,
        productType: PRODUCT_TYPE.PRODUCT_COMBO,
        __flexComboServerKeys: serverKeys,
      };
    },
    /**
     * Flat list for Vuex / OrderProductsTable from GET order `orderItems` (API rows).
     */
    flattenOrderApiItemsToVuexShape(orderItems, storeId) {
      if (!Array.isArray(orderItems) || orderItems.length === 0) {
        return [];
      }
      const itemHandlers = orderItems.map((item) => {
        const row = { ...item };
        row.sellingPrice = item.productPrice;
        row.sellingPriceMask = item.productPrice;
        row.discountType = item.discountType || 1;
        row.discountAmount = item.discountAmount ?? 0;
        row.productName = item.productName;
        row.productCode = item.productCode;
        row.productBarCode = item.productBarCode || item.barCode;
        row.imageUrl = item.imageUrl;
        row.comboItems =
          item.comboItems || item.productInCombos || item.listProductInCombo || [];
        row.repurchasePrice = item.repurchasePrice;
        row.belongOrderDetailId = item.belongOrderDetailId;
        row.checked = item.isGiftTaken;
        row.storeId = storeId;
        row.voucherId = item.voucherId ?? null;
        row.voucherDetailId = item.voucherDetailId ?? null;
        row.voucherCode = item.voucherCode ?? null;
        row.discountProgramId = item.discountProgramId ?? null;
        row.promotionProgramCode = item.promotionProgramCode || '';
        row.promotionProgramName = item.promotionProgramName || '';
        normalizePromotionLabelsFromHydrate(row, item);
        hydrateSplitAmountsFromApi(
          row,
          item.discountProgramAmount,
          item.voucherAmount,
        );
        row.flexibleComboId = item.flexibleComboId ?? null;
        row.flexibleComboItemId = item.flexibleComboItemId ?? null;
        row.flexibleComboName = item.flexibleComboName ?? null;
        row.flexibleComboCode = item.flexibleComboCode ?? null;
        row.attachLevel = item.attachLevel ?? resolveAttachLevel(item);
        row.promotionProgramId = item.promotionProgramId ?? item.accessoryGroupId ?? null;
        row.accessoryGroupId = item.accessoryGroupId ?? item.promotionProgramId ?? null;
        row.accessoryGroupCode = item.accessoryGroupCode ?? item.promotionProgramCode ?? '';
        return row;
      });

      const mainsOrdered = itemHandlers.filter(
        (i) => i.orderItemType === ITEM_TYPE.MAIN,
      );
      const mergedParents =
        this.mergeConsecutiveFlatFlexComboOrderMains(mainsOrdered);

      const term = [];
      let count = 1;
      mergedParents.forEach((parent) => {
        const keysForRelated = parent.__flexComboServerKeys
          ? parent.__flexComboServerKeys
          : [String(parent.orderItemId ?? parent.id)].filter(
              (k) => k && k !== 'undefined',
            );
        delete parent.__flexComboServerKeys;

        const parentId = uuidv4();
        parent.id = parentId;
        parent.stt = count;

        term.push(parent);

        const related = itemHandlers.filter(
          (child) =>
            child.orderItemType !== ITEM_TYPE.MAIN &&
            keysForRelated.some((k) => String(child.belongOrderDetailId) === k),
        );

        const gifts = related.filter((c) => c.orderItemType === ITEM_TYPE.GIFT);
        gifts.forEach((g) => {
          g.id = uuidv4();
          g.belongOrderDetailId = parentId;
          term.push(g);
        });

        const l1Attach = related.filter(
          (c) =>
            c.orderItemType === ITEM_TYPE.ATTACH && resolveAttachLevel(c) === 1,
        );

        l1Attach.forEach((l1) => {
          const l1ServerKey = String(l1.orderItemId ?? l1.id);
          const l1ClientId = uuidv4();
          l1.id = l1ClientId;
          l1.belongOrderDetailId = parentId;
          if (!l1.attachLevel) {
            l1.attachLevel = 1;
          }
          term.push(l1);

          const l2Attach = itemHandlers.filter(
            (c) =>
              c.orderItemType === ITEM_TYPE.ATTACH &&
              resolveAttachLevel(c) === 2 &&
              String(c.belongOrderDetailId) === l1ServerKey,
          );
          l2Attach.forEach((l2) => {
            l2.id = uuidv4();
            l2.belongOrderDetailId = l1ClientId;
            term.push(l2);
          });
        });

        count++;
      });
      return term;
    },
    getEmployeeFromLocal() {
      const userFromLocal = SaveDataUtil.getData('employee_info');
      if (userFromLocal) {
        return JSON.parse(userFromLocal);
      }
    },
    getJobTitleFromLocal(jobTitleId) {
      if (!jobTitleId) {
        return;
      }
      const data = !isNaN(Number(jobTitleId)) ? Number(jobTitleId) : 0;
      if (JOB_TITLE_NAME[data]) {
        return JOB_TITLE_NAME[data];
      }
      return 'NON_IDENTIFIED JOB TITLE';
    },
  },
};
export default commonMixin;
